import { IBackendError } from '@models/common/api';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import i18next from 'i18next';
import { stringify } from 'qs';
import { toast } from 'react-toastify';
import { attachAnalyticsConsentHeader } from 'src/libs/api/analytics-consent';
import { getAuthToken, isClerkAuthEnabled } from 'src/utils/clerkAuth';
import localStorageKeys from 'src/utils/localStorageKeys';

export const BACKEND_BASE_URL = `${import.meta.env.VITE_BACKEND_URL}`;

const axiosInstance = axios.create({
  baseURL: `${BACKEND_BASE_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    serialize: (params) => {
      return stringify(params, { arrayFormat: 'repeat' });
    },
  },
});

/**
 * Exported so the analytics-consent contract can be asserted in tests: this
 * client carries the majority of listing-review traffic, including every
 * endpoint whose Mixpanel tracking is consent-gated.
 */
export async function attachBackendRequestHeaders(request: InternalAxiosRequestConfig) {
  request.headers.set('Accept-Language', i18next.language);
  const token = await getAuthToken();
  if (token) {
    request.headers = request.headers || {};
    request.headers.Authorization = `Bearer ${token}`;
  }
  return attachAnalyticsConsentHeader(request);
}

axiosInstance.interceptors.request.use(attachBackendRequestHeaders);

export function handleBackendResponseError(error: AxiosError<IBackendError>): never {
  // Clerk owns session expiry. A 401 can come from a backend route that has not
  // opted into Clerk yet, so never destroy a valid Clerk session here.
  if (error.response?.status === 401 && !error.config?.url?.includes('magic-link')) {
    if (isClerkAuthEnabled()) throw error.response?.data;

    const countryCode = localStorage.getItem(localStorageKeys.commons.countryPreference);
    localStorage.clear();
    sessionStorage.clear();

    sessionStorage.setItem('logout_reason', 'token_expired');
    window.location.href = `/auth${countryCode && countryCode !== 'null' ? `?country=${countryCode}` : ''}`;
  }

  // Handle 402 Payment Required response
  if (error.response?.status === 402 && !error.config?.url?.includes('magic-link')) {
    toast.error(error.response.data.message);
  }
  throw error.response?.data;
}

axiosInstance.interceptors.response.use((response: AxiosResponse) => response, handleBackendResponseError);

export const get = <T = unknown>(url: string, config?: AxiosRequestConfig, version: number = 1) => {
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return axiosInstance.get<T>(`/v${version}${normalizedUrl}`, config);
};
export const post = <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig, version: number = 1) => {
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return axiosInstance.post<T>(`/v${version}${normalizedUrl}`, data, config);
};
export const put = <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig, version: number = 1) => {
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return axiosInstance.put<T>(`/v${version}${normalizedUrl}`, data, config);
};
export const patch = <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig, version: number = 1) => {
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return axiosInstance.patch<T>(`/v${version}${normalizedUrl}`, data, config);
};
export const del = <T = unknown>(url: string, config?: AxiosRequestConfig, version: number = 1) => {
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return axiosInstance.delete<T>(`/v${version}${normalizedUrl}`, config);
};

export const delWithBody = <T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
  version: number = 1,
) => {
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return axiosInstance.delete<T>(`/v${version}${normalizedUrl}`, { ...config, data });
};
